import { Box, Typography } from "@mui/material";
import React from "react";

import { useCelleraTheme } from "../../theme/useCelleraTheme";

export interface SidebarSection {
  id: string;
  label: string;
  description?: string;
}

interface SidebarLayoutProps {
  sections: SidebarSection[];
  selectedSectionId: string;
  onSectionChange: (sectionId: string) => void;
  children: React.ReactNode;
  sidebarWidth?: number;
}

const SidebarLayout: React.FC<SidebarLayoutProps> = ({
  sections,
  selectedSectionId,
  onSectionChange,
  children,
  sidebarWidth = 300,
}) => {
  const { colors } = useCelleraTheme();

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        border: `1px solid ${colors.border.light}`,
        borderRadius: "12px",
        overflow: "hidden",
        padding: 0,
      }}
    >
      {/* Left Sidebar Menu */}
      <Box
        sx={{
          width: `${sidebarWidth}px`,
          flexShrink: 0,
          borderRight: `1px solid ${colors.border.light}`,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {sections.map((section) => (
            <Box
              key={section.id}
              onClick={() => onSectionChange(section.id)}
              sx={{
                padding: "12px 16px",
                borderRadius: "8px",
                cursor: "pointer",
                backgroundColor:
                  selectedSectionId === section.id
                    ? colors.primary.light
                    : "transparent",
                border:
                  selectedSectionId === section.id
                    ? `1px solid ${colors.primary.main}`
                    : `1px solid transparent`,
                "&:hover": {
                  backgroundColor:
                    selectedSectionId === section.id
                      ? colors.primary.light
                      : colors.background.default,
                },
                transition: "all 0.2s ease-in-out",
                flexShrink: 0,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: selectedSectionId === section.id ? 600 : 400,
                  color:
                    selectedSectionId === section.id
                      ? colors.primary.main
                      : colors.text.primary,
                  mb: section.description ? 0.5 : 0,
                }}
              >
                {section.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Right Content Area */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 2,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default SidebarLayout;
